// ===========================================
// Class Weeks Configuration — which weeks of the term a plain class runs
// ===========================================
// One picker for INDIVIDUAL, GROUP and BAND classes (class weeks Phase 7, 2026-10-16;
// generalised from the band-only `BandTermConfig`). A BAND keeps its rehearsal copy and its
// derived "every week" preview; an INDIVIDUAL / GROUP class picks "Every week" or "Choose
// weeks", with a fortnightly quick-fill (plan D3: it writes an explicit list once, never a
// cadence). HYBRID classes use `HybridPatternConfig` instead.
// Plan: md/plan/individual-group-week-selection.md §9 T1/T2.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import InfoIcon from '@mui/icons-material/Info';
import { ClassWeeksFormData, ClassWeeksPatternType } from './lessons.types';
import { sameNumbers, resolveTermWeekOptions, termLengthsSentence, fortnightlyWeeks } from './lessons.helpers';
import { WEEK_NUMBERS } from './lessons.constants';
import { useBandTerm, useBandWeeksPreview } from '../../../hooks/useLessons';

/** The plain class types this picker serves. */
export type ClassWeeksLessonType = 'INDIVIDUAL' | 'GROUP' | 'BAND';

interface ClassWeeksConfigProps {
  /** INDIVIDUAL / GROUP: "Lesson weeks", no preview. BAND: rehearsal copy + derived preview. */
  lessonType: ClassWeeksLessonType;
  /** Disable every control (an all-locked class is read-only). Default false. */
  disabled?: boolean;
  lessonId: string;       // '' when creating (not yet saved)
  termId: string;
  formData: ClassWeeksFormData;
  onFormDataChange: (updates: Partial<ClassWeeksFormData>) => void;
  /** How many terms this class is being created across, or spans (for the multi-term note). */
  selectedTermCount?: number;
  /** All terms this class spans — the CUSTOM picker's bound is the LONGEST of them. */
  termIds?: string[];
  /** id → name for the selected terms, to caption a week a shorter term lacks. */
  termNames?: Record<string, string>;
  /**
   * id → Monday-anchored week count (`termWeekCount`). Supplied → the picker offers weeks
   * up to the longest selected term and CAPTIONS the ones a shorter term lacks ("not in
   * Term 4 2026") — never disabled, because week 11 may be right for Term 2 even though
   * Term 4 drops it on save (#173: the server clamps per term and reports it). Absent →
   * the transport ceiling 1–15, the pre-2026-09-23 behaviour. Identical rule and prop
   * shape to `HybridPatternConfig`; both call `resolveTermWeekOptions`.
   */
  termWeeks?: Record<string, number>;
  /**
   * The class window in the ANCHOR term (`termId`), when it has one: weeks outside it are captioned
   * "not scheduled in {term}" but stay selectable (they still apply to the other terms). Absent =
   * a full-term class, no caption. The fortnightly quick-fill stays inside it.
   */
  anchorWindow?: { termName: string; first: number; last: number } | null;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ClassWeeksConfig({
  lessonType,
  lessonId,
  termId,
  formData,
  onFormDataChange,
  selectedTermCount = 1,
  termIds = [],
  termNames,
  termWeeks,
  anchorWindow,
  disabled = false,
}: ClassWeeksConfigProps) {
  const isBand = lessonType === 'BAND';
  const hasInitialized = useRef(false);

  // Bound the CUSTOM picker to the terms this class actually spans (#173 Phase 3), using
  // the same rule as the hybrid picker — offer up to the LONGEST selected term, caption
  // (never disable) a week a shorter term lacks, and say so once when they differ.
  const { selectedTermWeeks, weekOptions, termLengthsDiffer, weekLabel } = useMemo(
    () => resolveTermWeekOptions(WEEK_NUMBERS, termIds, termId, termNames, termWeeks, anchorWindow),
    [termIds, termId, termNames, termWeeks, anchorWindow]
  );

  // BAND only: fetch the stored row (only when lessonId is non-empty). It gates the derived
  // preview below (#181). An INDIVIDUAL / GROUP class derives nothing, and the page already
  // seeds the form from the freshly loaded lesson, so it fetches nothing here — re-seeding
  // from a second copy would only undo the admin's unsaved edits (a type switch to Hybrid and
  // back re-mounts this; /qa class weeks Phase 7 L1) or, from a stale cache, write old weeks
  // back on the next save (/qa Phase 7 H1).
  const { data: existingRow, isFetchedAfterMount, isRefetchError } = useBandTerm(isBand ? lessonId : '', termId);

  // Has the stored row had its say? In edit mode NOTHING derived may exist before it has
  // (#181). It is STATE, not the `hasInitialized` ref: a ref mutation does not re-render,
  // so nothing downstream would observe the change. `useBandTerm` settles to `null` when
  // the lesson has no row yet, which still counts as settled — otherwise a band that has
  // never been configured could never fill its weeks.
  const [storedSettled, setStoredSettled] = useState(!lessonId || !isBand);

  // BAND only: auto-calculate weeks preview when EVERY_WEEK is selected. An INDIVIDUAL /
  // GROUP class has nothing to derive (and the route 400s for it: `calculateBandWeeks`).
  // ⚠️ Gated on `storedSettled` (#181): the query's own enable condition reads
  // `formData.patternType`, which on a fresh dialog is DEFAULT_FORM_DATA's 'EVERY_WEEK'
  // even when the saved row is CUSTOM (a multi-term band whose grouped row is EVERY_WEEK,
  // or a BandTerm stranded on another term by #175). Without this gate the preview
  // fetched against that default, and its derived weeks overwrote the admin's own choice
  // a render later — measured: stored CUSTOM [3,6,9] became CUSTOM [1..10], silently, on
  // a room-only edit. Gating the QUERY (not just the effect that consumes it) is what
  // makes the race unrepresentable: the derived data cannot exist too early.
  const { data: bandWeeksPreview, isLoading: isPreviewLoading } = useBandWeeksPreview(
    isBand && lessonId && storedSettled && formData.patternType === 'EVERY_WEEK' ? lessonId : '',
    termId
  );

  // BAND: initialize form data from the server when editing an existing row.
  // ⚠️ Only from a copy fetched for THIS open (`isFetchedAfterMount`): React Query returns the
  // cached row first, and one older than the last save would seed the form with old weeks that
  // the next save writes back (/qa class weeks Phase 7 H1). `=== false`, not `!`: a test double
  // that omits the flag stands for an already-fresh row.
  useEffect(() => {
    if (!isBand) return;
    if (existingRow === undefined || isFetchedAfterMount === false) return; // still loading / stale
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      // ⚠️ A FAILED refetch also sets `isFetchedAfterMount`, but `data` is then still the cached
      // (possibly stale) row: keep the page's fresh seed instead (/qa whole feature B-L5).
      if (existingRow && !isRefetchError) {
        onFormDataChange({
          patternType: existingRow.patternType,
          weeks: existingRow.weeks ?? existingRow.rehearsalWeeks ?? [],
        });
      }
    }
    setStoredSettled(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBand, existingRow, isFetchedAfterMount, isRefetchError]);

  // BAND: auto-sync preview weeks to form when EVERY_WEEK and preview data is available.
  // ⚠️ `patternType` MUST be in the deps (#177): the effect READS it, so leaving it out
  // means the effect cannot re-run when only it changes. That matters whenever
  // `bandWeeksPreview` holds the same reference across the switch — a React Query cache
  // hit (the admin toggles EVERY_WEEK → CUSTOM → EVERY_WEEK, second switch served warm)
  // or any render where the data is already settled. The weeks `handlePatternTypeChange`
  // just cleared then stay empty. (⚠️ The query IS disabled while CUSTOM — see the
  // `enabled` gate above — so on a FIRST switch it genuinely refetches and the old deps
  // happened to work. Do not read this comment as "the cache is always warm".)
  // Re-running on an unchanged preview is a no-op: the guard below skips the write when
  // the value already matches.
  useEffect(() => {
    // No `storedSettled` check needed here — the QUERY above is gated on it, so
    // `bandWeeksPreview` is undefined until the stored row has spoken (#181).
    if (!isBand || formData.patternType !== 'EVERY_WEEK' || !bandWeeksPreview) return;
    if (sameNumbers(formData.weeks, bandWeeksPreview)) return; // no self-retriggering loop
    onFormDataChange({ weeks: bandWeeksPreview });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBand, bandWeeksPreview, formData.patternType, formData.weeks]);

  // ===========================================
  // FORTNIGHTLY QUICK-FILL (D3)
  // ===========================================
  // From a start week, every second week up to the class window's last week (or the
  // longest selected term). It only writes a list; nothing about "fortnightly" is stored.
  const longestWeek = weekOptions[weekOptions.length - 1] ?? 1;
  const hasWindow = !!anchorWindow && anchorWindow.first <= anchorWindow.last;
  const fillLast = hasWindow ? Math.min(anchorWindow!.last, longestWeek) : longestWeek;
  const fillFirst = Math.min(hasWindow ? anchorWindow!.first : 1, fillLast);
  const startOptions = weekOptions.filter((w) => w >= fillFirst && w <= fillLast);
  const [chosenStart, setChosenStart] = useState<number | null>(null);
  const fortnightStart = chosenStart !== null && startOptions.includes(chosenStart) ? chosenStart : fillFirst;
  const otherStart = fortnightStart + 1;

  const fillFortnightly = (start: number) => {
    onFormDataChange({ patternType: 'CUSTOM', weeks: fortnightlyWeeks(start, fillLast) });
  };

  // ===========================================
  // EVENT HANDLERS
  // ===========================================

  const handlePatternTypeChange = (e: SelectChangeEvent<ClassWeeksPatternType>) => {
    onFormDataChange({
      patternType: e.target.value as ClassWeeksPatternType,
      weeks: [],
    });
  };

  const handleWeeksChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    const weeks = typeof value === 'string' ? value.split(',').map(Number) : value;
    onFormDataChange({ weeks: [...weeks].sort((a, b) => a - b) });
  };

  // ===========================================
  // RENDER
  // ===========================================

  const isEditMode = !!lessonId;
  const customEmpty = formData.patternType === 'CUSTOM' && formData.weeks.length === 0;
  const copy = isBand
    ? {
        heading: 'Rehearsal Week Configuration',
        caption: 'Configure which weeks this band has rehearsals',
        patternLabel: 'Rehearsal Pattern',
        everyWeek: 'Every Week (auto-calculate)',
        custom: 'Custom (pick weeks manually)',
      }
    : {
        heading: 'Lesson Weeks',
        caption: 'Which weeks of the term this class runs: every week, or chosen weeks such as fortnightly',
        patternLabel: 'Lesson weeks',
        everyWeek: 'Every week',
        custom: 'Choose weeks',
      };
  const everyWeekRange = hasWindow
    ? anchorWindow!.first === anchorWindow!.last
      ? `week ${anchorWindow!.first}`
      : `weeks ${anchorWindow!.first}–${anchorWindow!.last}`
    : termWeeks && selectedTermWeeks.length > 0
      ? `weeks 1–${longestWeek}`
      : null;

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {copy.heading}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {copy.caption}
        </Typography>
      </Grid>

      {/* Pattern Type Selector */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required disabled={disabled}>
          <InputLabel>{copy.patternLabel}</InputLabel>
          <Select
            value={formData.patternType}
            label={copy.patternLabel}
            onChange={handlePatternTypeChange}
            SelectDisplayProps={{ 'data-testid': 'class-weeks-pattern-select' } as React.HTMLAttributes<HTMLDivElement>}
          >
            <MenuItem value="EVERY_WEEK">{copy.everyWeek}</MenuItem>
            <MenuItem value="CUSTOM">{copy.custom}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* EVERY_WEEK, BAND: the auto-calculated weeks as read-only chips */}
      {isBand && formData.patternType === 'EVERY_WEEK' && (
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              minHeight: 56,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: '100%', mb: 0.5 }}
            >
              Rehearsal Weeks (auto-calculated)
            </Typography>
            {!isEditMode && (
              <Alert severity="info" sx={{ width: '100%', py: 0.5, mb: 0 }}>
                Save the lesson first to auto-calculate weeks.
              </Alert>
            )}
            {isEditMode && isPreviewLoading ? (
              <CircularProgress size={16} />
            ) : isEditMode && formData.weeks.length > 0 ? (
              formData.weeks.map((w) => (
                <Chip key={w} label={`Wk ${w}`} size="small" color="info" />
              ))
            ) : (
              <Typography variant="caption" color="text.disabled">
                {isEditMode ? 'Loading...' : 'Create the lesson first'}
              </Typography>
            )}
          </Box>
        </Grid>
      )}

      {/* EVERY_WEEK, INDIVIDUAL / GROUP: nothing to derive — say what it means */}
      {!isBand && formData.patternType === 'EVERY_WEEK' && (
        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" data-testid="class-weeks-every-week">
            Runs every week of the class{everyWeekRange ? ` (${everyWeekRange})` : ''}.
          </Typography>
        </Grid>
      )}

      {/* CUSTOM Mode: Multi-select dropdown for weeks */}
      {formData.patternType === 'CUSTOM' && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required disabled={disabled} error={customEmpty}>
            <InputLabel>Select Weeks</InputLabel>
            <Select
              multiple
              value={formData.weeks}
              label="Select Weeks"
              onChange={handleWeeksChange}
              renderValue={(selected) => selected.join(', ')}
              SelectDisplayProps={{ 'data-testid': 'class-weeks-select' } as React.HTMLAttributes<HTMLDivElement>}
            >
              {weekOptions.map((week) => (
                <MenuItem key={week} value={week} sx={{ whiteSpace: 'normal' }}>
                  {weekLabel(week)}
                </MenuItem>
              ))}
            </Select>
            {customEmpty && (
              <FormHelperText data-testid="class-weeks-empty-error">
                Choose at least one week, or switch to {isBand ? 'Every Week' : 'Every week'}.
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      )}

      {/* Fortnightly quick-fill (D3): fills the list once; the admin can then edit it. Only once a
          term is chosen: before that the bound is the 15-week transport cap, not a term (/qa L2). */}
      {startOptions.length > 0 && selectedTermWeeks.length > 0 && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Quick fill:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
              <InputLabel>From week</InputLabel>
              <Select
                value={fortnightStart}
                label="From week"
                onChange={(e) => setChosenStart(Number(e.target.value))}
                SelectDisplayProps={{ 'data-testid': 'class-weeks-fortnight-start' } as React.HTMLAttributes<HTMLDivElement>}
              >
                {startOptions.map((week) => (
                  <MenuItem key={week} value={week}>
                    Week {week}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() => fillFortnightly(fortnightStart)}
              data-testid="class-weeks-fortnightly"
            >
              Fortnightly from week {fortnightStart}
            </Button>
            <Button
              size="small"
              variant="text"
              disabled={disabled || otherStart > fillLast}
              onClick={() => fillFortnightly(otherStart)}
              data-testid="class-weeks-other-fortnight"
            >
              Other fortnight (from week {otherStart})
            </Button>
          </Box>
        </Grid>
      )}

      {formData.patternType === 'CUSTOM' && termLengthsDiffer && (
        <Grid item xs={12}>
          <Alert severity="info" data-testid="class-weeks-term-lengths-differ">
            The selected terms differ in length: {termLengthsSentence(selectedTermWeeks)}. A
            {isBand ? ' rehearsal' : ''} week beyond a term&apos;s end is dropped from that term when you
            save, and you&apos;ll be told which.
          </Alert>
        </Grid>
      )}

      {/* Multi-term note — a class across several terms has one row per term. */}
      {selectedTermCount > 1 && (
        <Grid item xs={12}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1 }}>
            {isBand
              ? `This band will be created in ${selectedTermCount} terms — one per term.`
              : `This class spans ${selectedTermCount} terms.`}
            {formData.patternType === 'CUSTOM'
              ? ' The selected week numbers apply to each term (a week number can fall on a different date in each term), and a week beyond a term’s end is dropped from that term when you save — you’ll be told which.'
              : isBand
                ? ' "Every week" rehearsal weeks are calculated separately for each term.'
                : ' It runs every week of each term.'}
          </Alert>
        </Grid>
      )}

      {/* Info Message */}
      {isBand && (
        <Grid item xs={12}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1 }}>
            Rehearsal configuration is saved automatically when you save the lesson.
          </Alert>
        </Grid>
      )}
    </>
  );
}
